import React from 'react';
import { ReactComponent as XIcon } from "@shared/assets/Icons/IconCrossSquare.svg";

interface TagProps {
  label: string;
  variant?: 'success' | 'error' | 'warning' | 'default' | 'neutral' | 'custom';
  backgroundColor?: string; 
  textColor?: string;
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  bordered?: boolean;
  rounded?: 'none' | 'small' | 'medium' | 'full';
  minWidth?: string;
  removable?: boolean
  onRemove?: () => void
  /** Optional extra classname(s) */
  className?: string;
  /** Click handler for when the tag itself is clicked */
  onClick?: () => void;
}

const sizeClasses = {
  small: 'text-xs px-2 py-1',
  medium: 'text-sm px-3 py-1.5',
  large: 'text-base px-4 py-2',
};

const roundedClasses = {
  none: 'rounded-none',
  small: 'rounded-sm',
  medium: 'rounded-md',
  full: 'rounded-full',
};

const variantClasses = {
    success: 'bg-semantic-bg-success-secondary text-semantic-text-success',
    error: 'bg-semantic-bg-danger-secondary text-semantic-text-danger',
    warning: 'bg-semantic-bg-warning-secondary text-semantic-text-warning',
    default: 'bg-gray-100 text-black',
    neutral: 'bg-semantic-bg-brand-secondary text-semantic-text-secondary',
    custom: '',
};

export const GenericTag: React.FC<TagProps> = ({
    label,
    variant = 'default',
    backgroundColor,
    textColor,
    icon,
    size = 'small',
    bordered = false,
    rounded = 'medium',
    minWidth,
    removable = false,
    onRemove,
    className='',
    onClick,
  }) => {
    const borderClass = bordered ? 'border border-gray-400' : '';
    
    const variantClass = variantClasses[variant];
    let bgClass = '';
    let customBgStyle = {};
    let finalTextColor = '';

    if (variant === 'custom') {
      if (backgroundColor) {
        if (backgroundColor.startsWith('#')) {
          customBgStyle = { backgroundColor };
        } else {
          bgClass = backgroundColor;
        }
      }
      finalTextColor = textColor || '';
    } else {
      bgClass = backgroundColor
        ? backgroundColor.startsWith('#')
          ? ''
          : backgroundColor
        : variantClass.split(' ')[0];
      customBgStyle = backgroundColor?.startsWith('#') ? { backgroundColor } : {};
      finalTextColor = textColor || variantClass.split(' ')[1];
    }

    const inlineStyles = {
      ...customBgStyle,
      ...(minWidth ? { minWidth } : {}),
    };
    const clickableClass = onClick ? 'cursor-pointer' : '';

    return (
        <div
          className={`inline-flex items-center gap-2 ${sizeClasses[size]} ${bgClass} ${finalTextColor} ${borderClass} ${roundedClasses[rounded]}`}
          style={inlineStyles}
        >
          <div
            onClick={onClick}
            className={`inline-flex items-center gap-2
              ${sizeClasses[size]} ${bgClass} ${finalTextColor}
              ${borderClass} ${roundedClasses[rounded]}
              ${clickableClass} ${className}`}
            style={inlineStyles}
          >
            {icon && <span>{icon}</span>}
            <span>{label}</span>
            {removable && onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="p-1 ml-1 rounded hover:bg-gray-200 focus:outline-none"
              >
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      );
    };
